import React, { Suspense, lazy, useContext, useState } from "react";

import Image from "next/image";
import Link from "next/link";

import { Popover } from "antd";
import { FaStar } from "react-icons/fa";
import { FiSliders } from "react-icons/fi";
import { RiTrophyFill } from "react-icons/ri";

import { Bag } from "shared/assets/icons/Bag";
import CircularLoader from "shared/components/atoms/CircularLoader";
import { ComponentErrorBoundary } from "shared/components/atoms/ComponentErrorBoundary";
import { useOrganizationConfig } from "shared/hooks/useOrganizationConfig";
import { ArrowIcon } from "shared/providers/Layout/components/NewTopBar/assets/ArrowIcon";
import { ConfigIcon } from "shared/providers/Layout/components/NewTopBar/assets/ConfigIcon";
import { LogoutIcon } from "shared/providers/Layout/components/NewTopBar/assets/LogoutIcon";
import { UserIcon } from "shared/providers/Layout/components/NewTopBar/assets/UserIcon";
import { SwitchTheme } from "shared/providers/Layout/components/NewTopBar/components/atoms/SwitchTheme";
import { useTopbarContext } from "shared/providers/Layout/hooks/useTopbarContext";
import GlobalContext from "shared/providers/context/GlobalContext";
import capitalizeFirstLetter from "shared/utils/capitalizeFirstLetter";
import componentLoader from "shared/utils/componentLoader";
import { handleSplitName } from "shared/utils/handleSplitName";
import { useAuthContext } from "shared/utils/hooks/useAuthContext";
import { useFeature } from "shared/utils/hooks/useFeature";
import { useGlobalContext } from "shared/utils/hooks/useGlobalContext";

import { hasRequiredPermissions } from "@Console/utils/checkHasPermission";
import { permissionsAccessAdminConsole } from "@Console/utils/permissionsAreasAdminConsole";

import MyPanelIcon from "../../../assets/MyPanelIcon";
import GamificationMenuInfos from "../GamificationMenuInfos";
import { OptionsContainer, UserMenuContainer } from "./styles";

const OrganizationSelect = lazy(() =>
  componentLoader(
    () =>
      import(
        /* webpackChunkFilename: "OrganizationSelect", filename: "OrganizationSelect" */
        "shared/providers/Layout/components/NewTopBar/components/molecules/OrganizationSelect"
      ),
  ),
);

export const Options = () => {
  const { signOut, loggedUser: user, rolesUser } = useAuthContext();

  const isStudentProfileEnabled = useFeature("f_user_profile");

  const { shouldAllowUserChangeOrganization } = useOrganizationConfig();
  const { state } = useContext(GlobalContext);

  const isCoProducer = user?.has_recipient && !user?.promoter;

  const hasPermissionAdminConsole = hasRequiredPermissions(
    rolesUser,
    permissionsAccessAdminConsole,
  );
  const isPromoter = user?.promoter;
  const userIsLogged = !!user;
  const isGlobalGamificationEnabled = useFeature("f_global_gamification");
  const isStoreEnabled = useFeature("f_rewards_shop");

  const { setState } = useGlobalContext();
  const handleOpenAuthModal = () =>
    setState((prev) => ({
      ...prev,
      modal: {
        open: true,
        page: "first",
      },
    }));

  const gamificationData = state?.tenancy?.settings?.gamification;

  const isGamificationEnabled = gamificationData?.enableGamification == 1;
  const isRankingEnabled = gamificationData?.enableGlobalRanking == 1;
  const isRewardsShop = gamificationData?.enableRewardsShop == 1;

  const shouldShowRanking =
    isGlobalGamificationEnabled &&
    isGamificationEnabled &&
    userIsLogged &&
    isRankingEnabled;

  const shouldShowStore =
    isGlobalGamificationEnabled &&
    userIsLogged &&
    isStoreEnabled &&
    isRewardsShop;

  return (
    <ComponentErrorBoundary>
      <OptionsContainer>
        {userIsLogged && (
          <div className="user-info">
            <h1>{capitalizeFirstLetter(user?.display_name)}</h1>

            {isGlobalGamificationEnabled && isGamificationEnabled && (
              <GamificationMenuInfos />
            )}
          </div>
        )}

        {shouldAllowUserChangeOrganization && (
          <ComponentErrorBoundary>
            <Suspense fallback={<CircularLoader className="loader" />}>
              <OrganizationSelect />
            </Suspense>
          </ComponentErrorBoundary>
        )}

        <ul>
          {userIsLogged && (
            <>
              <li>
                <Link href={`/user/${user?.username}`} prefetch={false}>
                  <UserIcon />
                  Perfil
                </Link>
              </li>

              {isStudentProfileEnabled && (
                <li>
                  <Link href="/foryou" prefetch={false}>
                    <MyPanelIcon /> Meu painel
                  </Link>
                </li>
              )}
            </>
          )}
          {(hasPermissionAdminConsole || isCoProducer) && (
            <li>
              <Link href="/console" prefetch={false}>
                <ConfigIcon /> Admin Console
              </Link>
            </li>
          )}
          {isPromoter && (
            <li>
              <Link href="/cp/pixel" prefetch={false}>
                <ConfigIcon /> Painel de Controle
              </Link>
            </li>
          )}
          {shouldShowRanking && (
            <li>
              <Link href="/ranking">
                <RiTrophyFill /> Ranking geral
              </Link>
            </li>
          )}
          {shouldShowStore && (
            <li>
              <Link href="/rewards">
                <FaStar /> Loja de recompensas
              </Link>
            </li>
          )}

          {userIsLogged && (
            <>
              <li>
                <Link href="/orders" prefetch={false}>
                  <Bag /> Minhas compras
                </Link>
              </li>

              <li>
                <Link href="/account" prefetch={false}>
                  <FiSliders /> Minha conta
                </Link>
              </li>
            </>
          )}
          <li>
            <SwitchTheme />
          </li>

          <li>
            {userIsLogged ? (
              <button onClick={signOut}>
                <LogoutIcon /> Deslogar
              </button>
            ) : (
              <button onClick={handleOpenAuthModal}>
                <UserIcon /> Entrar / Criar conta
              </button>
            )}
          </li>
        </ul>
      </OptionsContainer>
    </ComponentErrorBoundary>
  );
};

export const UserMenu = () => {
  const [isLoadingImage, setIsLoadingImage] = useState(true);
  const [failedToLoadImage, setFailedToLoadImage] = useState(false);
  const [optionsIsOpen, setOptionsIsOpen] = useState(false);

  const { scrollIsOnTop, pageIsLoading } = useTopbarContext();

  const { loggedUser } = useAuthContext();
  const userHavePhoto = !!loggedUser?.photo && !failedToLoadImage;

  return (
    <UserMenuContainer
      optionsIsOpen={optionsIsOpen}
      scrollIsOnTop={scrollIsOnTop || pageIsLoading}
    >
      <Popover
        placement="bottomRight"
        content={
          <ComponentErrorBoundary>
            <Options />
          </ComponentErrorBoundary>
        }
        trigger={["click"]}
        className="popover"
        data-testid="popover"
        onOpenChange={() => setOptionsIsOpen(!optionsIsOpen)}
        overlayClassName="topbar-usermenu-popover-container"
      >
        <div className="avatar">
          {loggedUser ? (
            userHavePhoto ? (
              <Image
                alt=""
                src={loggedUser?.photo}
                width={30}
                height={30}
                onError={() => setFailedToLoadImage(true)}
                onLoadingComplete={() => setIsLoadingImage(false)}
                data-is-loading={isLoadingImage ? "true" : "false"}
              />
            ) : (
              <span>{handleSplitName(loggedUser?.full_name)}</span>
            )
          ) : (
            <UserIcon />
          )}
        </div>

        <ArrowIcon />
      </Popover>
    </UserMenuContainer>
  );
};
